/**
 * Calendário de obrigações do DP, PURO.
 *
 * Existe porque nenhum SaaS de RH brasileiro traz isso dentro do produto: o DP
 * consome calendário de obrigação como post de blog, PDF de escritório
 * contábil e story de Instagram. Trazer para dentro, com a data já calculada
 * para a competência aberta, é diferencial concreto e custa uma função pura.
 *
 * REGRA DESTE ARQUIVO: nenhuma data é constante de calendário. Tudo é
 * calculado a partir da competência, porque "dia 20" cai em sábado uma vez a
 * cada sete meses e a obrigação ANTECIPA, não posterga.
 */
const dia = (d) => d.toISOString().slice(0, 10);
const dow = (d) => d.getUTCDay();
/** Feriado nacional FIXO. Móvel (Carnaval, Corpus Christi) depende da Páscoa. */
function feriadosFixos(ano) {
    return new Set([
        `${ano}-01-01`, `${ano}-04-21`, `${ano}-05-01`, `${ano}-09-07`,
        `${ano}-10-12`, `${ano}-11-02`, `${ano}-11-15`, `${ano}-11-20`, `${ano}-12-25`,
    ]);
}
/** Páscoa pelo algoritmo de Gauss/Meeus. Move Carnaval, Sexta Santa e Corpus Christi. */
export function pascoa(ano) {
    const a = ano % 19, b = Math.floor(ano / 100), c = ano % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mes = Math.floor((h + l - 7 * m + 114) / 31);
    const diaDoMes = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(ano, mes - 1, diaDoMes));
}
function feriados(ano) {
    const s = feriadosFixos(ano);
    const p = pascoa(ano);
    const desloca = (dias) => {
        const d = new Date(p);
        d.setUTCDate(d.getUTCDate() + dias);
        return dia(d);
    };
    s.add(desloca(-48)); // Carnaval, segunda
    s.add(desloca(-47)); // Carnaval, terça
    s.add(desloca(-2)); // Sexta-feira Santa
    s.add(desloca(60)); // Corpus Christi
    return s;
}
export const ehDiaUtil = (d, f = feriados(d.getUTCFullYear())) => dow(d) !== 0 && dow(d) !== 6 && !f.has(dia(d));
/**
 * Obrigação que cai em dia não útil ANTECIPA.
 *
 * É o inverso do que a intuição diz e o inverso do vencimento bancário comum:
 * em obrigação trabalhista e previdenciária, cair no sábado significa pagar na
 * sexta. Postergar é multa.
 */
export function antecipaParaDiaUtil(data, f) {
    const d = new Date(data);
    while (!ehDiaUtil(d, f))
        d.setUTCDate(d.getUTCDate() - 1);
    return d;
}
/** O 5º dia útil do mês, que é o prazo do salário (CLT art. 459 §1º). */
export function quintoDiaUtil(ano, mes, f) {
    const d = new Date(Date.UTC(ano, mes - 1, 1));
    let uteis = 0;
    while (true) {
        if (ehDiaUtil(d, f))
            uteis++;
        if (uteis === 5)
            return d;
        d.setUTCDate(d.getUTCDate() + 1);
    }
}
export function ultimoDiaUtil(ano, mes, f) {
    const d = new Date(Date.UTC(ano, mes, 0));
    return antecipaParaDiaUtil(d, f);
}
/**
 * As obrigações que vencem DENTRO da competência informada.
 *
 * A referência é sempre o mês ANTERIOR: a folha de agosto é paga e declarada em
 * setembro. Trocar os dois é o erro que faz o painel apontar o prazo errado.
 */
export function obrigacoesDe(competencia) {
    const [ano, mes] = competencia.split("-").map(Number);
    const f = feriados(ano);
    // Um mês pode cruzar o ano na referência: janeiro se refere a dezembro.
    const ref = mes === 1 ? `${ano - 1}-12` : `${ano}-${String(mes - 1).padStart(2, "0")}`;
    const noDia = (n) => dia(antecipaParaDiaUtil(new Date(Date.UTC(ano, mes - 1, n)), f));
    const lista = [
        { chave: "salario", nome: "Pagamento de salários", tipo: "pagamento",
            base: "CLT art. 459, §1º: até o 5º dia útil do mês seguinte",
            vence: dia(quintoDiaUtil(ano, mes, f)), referencia: ref },
        { chave: "esocial.fechamento", nome: "Fechamento do eSocial", tipo: "declaracao",
            base: "S-1200, S-1210 e o S-1299 que fecha a competência",
            vence: noDia(15), referencia: ref },
        { chave: "reinf", nome: "EFD-Reinf", tipo: "declaracao",
            base: "Mesma data do fechamento do eSocial",
            vence: noDia(15), referencia: ref },
        { chave: "sst", nome: "Eventos de SST", tipo: "declaracao",
            base: "S-2220 monitoramento da saúde, S-2240 condições ambientais",
            vence: noDia(15), referencia: ref },
        { chave: "dctfweb", nome: "DARF da DCTFWeb", tipo: "guia",
            base: "Contribuição previdenciária e IRRF retido",
            vence: noDia(20), referencia: ref },
        // O FGTS NÃO vence mais no dia 7. Com o FGTS Digital a guia passou para o
        // dia 20, e boa parte do conteúdo publicado sobre DP ainda diz 7.
        { chave: "fgts", nome: "Guia do FGTS Digital", tipo: "guia",
            base: "GFD. Não é mais dia 7: com o FGTS Digital passou para o dia 20",
            vence: noDia(20), referencia: ref },
        { chave: "consignado", nome: "Guia do consignado", tipo: "guia",
            base: "Crédito do Trabalhador, pelo FGTS Digital",
            vence: noDia(20), referencia: ref },
        { chave: "credito.averbacao", nome: "Averbações do Crédito do Trabalhador",
            tipo: "prazo", base: "Consulta e download no Emprega Brasil. Margem de 35%",
            vence: noDia(21), referencia: ref },
        { chave: "mit", nome: "Fechamento do MIT e transmissão da DCTFWeb",
            tipo: "declaracao", base: "Módulo de Inclusão de Tributos",
            vence: dia(ultimoDiaUtil(ano, mes, f)), referencia: ref },
    ];
    // Anuais, incluídas só no mês em que existem.
    if (mes === 2) {
        lista.push({ chave: "informe", nome: "Informe de rendimentos", tipo: "declaracao",
            base: "Entrega ao trabalhador até o último dia útil de fevereiro",
            vence: dia(ultimoDiaUtil(ano, 2, f)), referencia: `${ano - 1}` });
    }
    if (mes === 3 || mes === 9) {
        lista.push({ chave: "transparencia",
            nome: "Relatório de Transparência Salarial", tipo: "declaracao",
            base: "Lei 14.611/2023, empresas com 100 ou mais empregados. Multa de até 3% da folha",
            vence: dia(ultimoDiaUtil(ano, mes, f)), referencia: `${ano}` });
    }
    if (mes === 11) {
        lista.push({ chave: "decimo.1", nome: "1ª parcela do 13º salário", tipo: "pagamento",
            base: "Adiantamento até 30 de novembro, sem descontos, com FGTS",
            vence: dia(antecipaParaDiaUtil(new Date(Date.UTC(ano, 10, 30)), f)), referencia: `${ano}` });
    }
    if (mes === 12) {
        lista.push({ chave: "decimo.2", nome: "2ª parcela do 13º salário", tipo: "pagamento",
            base: "Até 20 de dezembro, com INSS e IRRF",
            vence: noDia(20), referencia: `${ano}` });
    }
    return lista.sort((a, b) => a.vence.localeCompare(b.vence));
}
/** Dias que faltam. Negativo é atraso, e atraso em DP é multa. */
export const diasAte = (vence, hoje) => Math.round((Date.parse(`${vence}T00:00:00Z`) - Date.parse(`${hoje}T00:00:00Z`)) / 86_400_000);
/**
 * O que vem PELA FRENTE, atravessando a virada do mês.
 *
 * "Obrigações do mês" morre no dia 21: tudo já venceu e o cartão vira uma
 * lista cinza que não diz o que fazer. O DP não trabalha por mês civil, ele
 * trabalha pelo próximo prazo, e no dia 25 de agosto o próximo prazo é de
 * setembro.
 *
 * Devolve o que ainda vence dentro da janela, e SÓ ENTÃO completa com as que
 * já passaram na competência corrente, porque conferir o que venceu também é
 * trabalho de DP.
 */
export function proximasObrigacoes(hoje, diasAFrente = 45) {
    const [ano, mes] = hoje.slice(0, 7).split("-").map(Number);
    const seguinte = mes === 12 ? `${ano + 1}-01` : `${ano}-${String(mes + 1).padStart(2, "0")}`;
    // Dois meses, porque uma janela de 45 dias sempre cruza a virada.
    const todas = [...obrigacoesDe(hoje.slice(0, 7)), ...obrigacoesDe(seguinte)]
        .sort((a, b) => a.vence.localeCompare(b.vence));
    return {
        aVencer: todas.filter((o) => {
            const d = diasAte(o.vence, hoje);
            return d >= 0 && d <= diasAFrente;
        }),
        venceram: todas
            .filter((o) => diasAte(o.vence, hoje) < 0 && o.vence.slice(0, 7) === hoje.slice(0, 7))
            .reverse(),
    };
}
//# sourceMappingURL=obrigacoes.js.map